import { CircleCheck, CircleX, Info } from 'lucide-react'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import styles from './Toast.module.css'

/**
 * Figma "Toast", Tone = Success | Error | Info: circle-check in the accent,
 * circle-x in the loss colour, info in the text colour — a "Something went
 * wrong" under a tick would read as a confirmation.
 */
export type ToastKind = 'success' | 'info' | 'error'

interface ToastItem {
  id: number
  message: string
  kind: ToastKind
  leaving: boolean
}

type Show = (message: string, kind?: ToastKind) => void

const ToastContext = createContext<Show>(() => undefined)

/** How long a confirmation stays. Long enough to read twice; it is status, not a control. */
const HOLD_MS = 3200
/** Matches --dur-nav so the leave animation finishes before the node is removed. */
const LEAVE_MS = 260

/**
 * Status that lives in the interface: a filled order, a closed position, an
 * address copied. It confirms that a significant action completed and gets
 * out of the way — never a modal, never something to dismiss. Announced
 * politely to screen readers through the live region.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const show = useCallback<Show>((message, kind = 'success') => {
    const id = nextId.current++
    setItems((current) => [...current.slice(-2), { id, message, kind, leaving: false }])
    setTimeout(() => {
      setItems((current) => current.map((item) => (item.id === id ? { ...item, leaving: true } : item)))
      setTimeout(() => setItems((current) => current.filter((item) => item.id !== id)), LEAVE_MS)
    }, HOLD_MS)
  }, [])

  const value = useMemo(() => show, [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.stack} role="status" aria-live="polite">
        {items.map((item) => (
          <div
            key={item.id}
            className={[styles.toast, item.leaving ? styles.leaving : ''].filter(Boolean).join(' ')}
          >
            {item.kind === 'success' ? (
              <CircleCheck className={styles.check} size={20} strokeWidth={1.5} aria-hidden="true" />
            ) : item.kind === 'error' ? (
              <CircleX className={styles.cross} size={20} strokeWidth={1.5} aria-hidden="true" />
            ) : (
              <Info className={styles.info} size={20} strokeWidth={1.5} aria-hidden="true" />
            )}
            <span className={styles.text}>{item.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): Show {
  return useContext(ToastContext)
}
